"use client";

import { Basket } from "@features/cartDetails/types";
import { useCheckoutDetails } from "@features/checkout/hooks/useCheckoutDetails";
import { fetcher, handle429s } from "@utils/fetchers";
import {
  useHydrated,
  useLatestCookieValue,
  useSupabaseSession,
  useThemeContext,
  useWindowSize,
} from "@utils/hooks";
import useSWR from "swr";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { joinClassNames } from "@utils/helpers";
import Link from "next/link";
import { useSelectedLayoutSegments } from "next/navigation";
import { Text } from "@components/theming";
import { useTranslations } from "next-intl";
import { useIsShopsite } from "@context/DomainContext";
import { useState } from "react";
import { motion } from "framer-motion";
import { isNameRequiredCompleted } from "@features/cartDetails/utils/cartUtils";
import StepTooltip from "./stepTooltip";
import { Skeleton } from "@radix-ui/themes";
import { Skeleton as SkeletonLoader } from "@components/wrapper";

export default function CheckoutSteps() {
  const checkoutTranslations = useTranslations("Checkout.Stepper");
  const isShopsite = useIsShopsite();
  const session = useSupabaseSession();
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const hydrated = useHydrated();
  const windowSize = useWindowSize();
  const theme = useThemeContext();
  const [hoveredStep, setHoveredStep] = useState<{
    width: number;
    x: number;
    index: number;
  } | null>(null);
  const { data: basket, isLoading: basketLoading } = useSWR<Basket>(
    basketKey ? `basket/${basketKey}` : null,
    fetcher
  );
  const { data: checkoutDetails } = useCheckoutDetails({
    swrOptions: {
      revalidateOnReconnect: false,
    },
  });
  const { error: formError } = useSWR(
    basketKey ? `basket/${basketKey}/form/status` : null,
    fetcher,
    {
      onErrorRetry: handle429s,
    }
  );

  const has_rego = basket?.HasRegoForms;

  const segments = useSelectedLayoutSegments();
  const pathname =
    segments.length > 0 ? `/cart/${segments[0]}/${segments[1]}` : "/cart";
  const navItems: { [x: string]: { name: string; show: boolean } } = {
    "/cart": {
      name: checkoutTranslations("cart"),
      show: true,
    },
    "/cart/checkout/form": {
      name: checkoutTranslations("form"),
      show: !!has_rego,
    },
    "/cart/checkout/sign-in": {
      name: checkoutTranslations("signin"),
      show: !session,
    },
    "/cart/checkout/payment": {
      name: checkoutTranslations("payment"),
      show: true,
    },
    "/cart/checkout/[order_id]/status": {
      name: checkoutTranslations("complete"),
      show: true,
    },
  };

  const paths = Object.entries(navItems).filter(([_, { show }]) => show);

  const currentStep_index = paths.findIndex(([path, _]) => path === pathname);

  if (!hydrated) {
    return (
      <div>
        <nav aria-label="Progress" className="block">
          <ol role="list" className="relative flex items-center flex-wrap">
            <SkeletonLoader
              sharedClasses="h-5 sm:h-6 animate-pulse w-10"
              whitelabelClasses="bg-[color:var(--gray-a5)] rounded-[var(--radius-2)]"
              shopsiteClasses="bg-gray-400 rounded-md"
            />
            <ChevronRightIcon
              className={joinClassNames(
                "ml-2 sm:ml-4 h-4 w-4 stroke-2",
                isShopsite ? "text-gray-400" : "text-[color:var(--gray-11)]"
              )}
            />
            <SkeletonLoader
              sharedClasses="ml-2 sm:ml-4 h-5 sm:h-6 animate-pulse w-16"
              whitelabelClasses="bg-[color:var(--gray-a5)] rounded-[var(--radius-2)]"
              shopsiteClasses="bg-gray-400 rounded-md"
            />
            <ChevronRightIcon
              className={joinClassNames(
                "ml-2 sm:ml-4 h-4 w-4 stroke-2",
                isShopsite ? "text-gray-400" : "text-[color:var(--gray-11)]"
              )}
            />
            <SkeletonLoader
              sharedClasses="ml-2 sm:ml-4 h-5 sm:h-6 animate-pulse w-20"
              whitelabelClasses="bg-[color:var(--gray-a5)] rounded-[var(--radius-2)]"
              shopsiteClasses="bg-gray-400 rounded-md"
            />
          </ol>
        </nav>
      </div>
    );
  }

  let currentStep_x = -4;
  for (let i = 0; i < currentStep_index; ++i) {
    currentStep_x +=
      document.getElementById(paths[i][0])?.getBoundingClientRect().width ?? 0;
  }
  currentStep_x +=
    currentStep_index * (windowSize.width && windowSize.width < 640 ? 32 : 48);

  const currentStep = {
    width:
      (document.getElementById(pathname)?.getBoundingClientRect().width ?? 0) +
      8,
    x: currentStep_x,
  };

  const max_navigable_index =
    basket && basket.Events
      ? !isNameRequiredCompleted(basket)
        ? 0
        : formError && has_rego
        ? 1
        : checkoutDetails && !checkoutDetails.GuestBooking && !session
        ? has_rego
          ? 2
          : 1
        : paths.length - 2 // we takeaway 2 from length cos the last page isnt navigable
      : 0;

  return (
    <div>
      {(basket && basket.Events && basket.Events.length > 0 && basketKey) ||
      basketLoading ? (
        <nav aria-label="Progress" className="block">
          <ol role="list" className="relative flex flex-wrap">
            {paths.map(([path, { name }], index) => {
              const isActive = path === pathname;

              if (
                (checkoutDetails?.IsPended &&
                  name === checkoutTranslations("cart")) ||
                index > max_navigable_index
              ) {
                return (
                  <Text
                    asChild
                    key={path}
                    sharedClasses={joinClassNames(
                      "relative z-10 flex items-center text-sm sm:text-base",
                      index > 0 ? "pl-2 sm:pl-4" : ""
                    )}
                    shopsiteClasses={
                      currentStep_index <= index
                        ? "text-gray-600"
                        : "text-indigo-500"
                    }
                    whitelabelClasses={
                      currentStep_index <= index
                        ? "text-[color:var(--gray-11)]"
                        : "text-[color:var(--accent-8)]"
                    }
                  >
                    <li
                      onMouseEnter={() => {
                        const width =
                          (document
                            .getElementById(
                              max_navigable_index < index
                                ? paths[max_navigable_index][0]
                                : path
                            )
                            ?.getBoundingClientRect().width ?? 0) + 8;
                        let x = -4;
                        for (
                          let i = 0;
                          i < Math.min(index, max_navigable_index);
                          ++i
                        ) {
                          x +=
                            document
                              .getElementById(paths[i][0])
                              ?.getBoundingClientRect().width ?? 0;
                        }
                        x +=
                          Math.min(index, max_navigable_index) *
                          (windowSize.width && windowSize.width < 640
                            ? 32
                            : 48);
                        setHoveredStep({
                          width,
                          x,
                          index: Math.min(index, max_navigable_index),
                        });
                      }}
                      onMouseLeave={() => setHoveredStep(null)}
                    >
                      <Skeleton
                        loading={basketLoading}
                        className={isShopsite ? "rounded-lg" : ""}
                      >
                        <StepTooltip
                          showTooltip={index > max_navigable_index}
                          onClose={() => setHoveredStep(null)}
                        >
                          <span id={path} className="cursor-not-allowed">
                            {name}
                          </span>
                        </StepTooltip>
                      </Skeleton>
                      {index !== paths.length - 1 ? (
                        <ChevronRightIcon
                          className={joinClassNames(
                            "ml-2 sm:ml-4 h-4 w-4 stroke-2",
                            isShopsite
                              ? currentStep_index <= index
                                ? "text-gray-400"
                                : "text-indigo-500"
                              : currentStep_index <= index
                              ? "text-[color:var(--gray-11)]"
                              : "text-[color:var(--accent-7)]"
                          )}
                        />
                      ) : null}
                    </li>
                  </Text>
                );
              }

              return (
                <Text
                  asChild
                  key={path}
                  sharedClasses={joinClassNames(
                    "relative z-10 flex items-center text-sm sm:text-base transition-color ease-in-out duration-200",
                    index > 0 ? "pl-2 sm:pl-4" : ""
                  )}
                  shopsiteClasses={
                    isActive || (hoveredStep && hoveredStep.index === index)
                      ? "text-indigo-600"
                      : currentStep_index <= index
                      ? "text-gray-600"
                      : "text-indigo-500"
                  }
                  whitelabelClasses={
                    isActive || (hoveredStep && hoveredStep.index === index)
                      ? theme && theme.appearance === "dark"
                        ? "text-[color:var(--accent-11)]"
                        : "text-[color:var(--accent-10)]"
                      : currentStep_index <= index
                      ? "text-[color:var(--gray-11)]"
                      : theme && theme.appearance === "dark"
                      ? "text-[color:var(--accent-10)]"
                      : "text-[color:var(--accent-9)]"
                  }
                >
                  <li
                    onMouseEnter={() => {
                      const width =
                        (document
                          .getElementById(
                            max_navigable_index < index
                              ? paths[max_navigable_index][0]
                              : path
                          )
                          ?.getBoundingClientRect().width ?? 0) + 8;
                      let x = -4;
                      for (
                        let i = 0;
                        i < Math.min(index, max_navigable_index);
                        ++i
                      ) {
                        x +=
                          document
                            .getElementById(paths[i][0])
                            ?.getBoundingClientRect().width ?? 0;
                      }
                      x +=
                        Math.min(index, max_navigable_index) *
                        (windowSize.width && windowSize.width < 640 ? 32 : 48);
                      setHoveredStep({ width, x, index });
                    }}
                    onMouseLeave={() => setHoveredStep(null)}
                  >
                    {path === "/cart/checkout/sign-in" &&
                    windowSize.width &&
                    windowSize.width <= 1024 ? (
                      // Below 1024px, skip the sign-in modal intercept and go
                      // straight to the full page - a plain anchor forces a
                      // hard navigation, bypassing Next's route interception
                      // (which triggers on any client-side navigation, not
                      // just Link clicks).
                      <a
                        id={path}
                        href={path}
                        className="w-max"
                        aria-current={isActive && "page"}
                      >
                        {name}
                      </a>
                    ) : (
                      <Link
                        id={path}
                        href={path}
                        className="w-max"
                        aria-current={isActive && "page"}
                      >
                        {name}
                      </Link>
                    )}
                    {index !== paths.length - 1 ? (
                      <ChevronRightIcon
                        className={joinClassNames(
                          "ml-2 sm:ml-4 h-4 w-4 stroke-2",
                          isShopsite
                            ? currentStep_index <= index
                              ? "text-gray-400"
                              : "text-indigo-500"
                            : currentStep_index <= index
                            ? "text-[color:var(--gray-11)]"
                            : theme && theme.appearance === "dark"
                            ? "text-[color:var(--accent-10)]"
                            : "text-[color:var(--accent-9)]"
                        )}
                      />
                    ) : null}
                  </li>
                </Text>
              );
            })}
            <motion.div
              key={paths.map(([_, { name }]) => name).join("-")}
              animate={
                hoveredStep
                  ? { width: hoveredStep.width, x: hoveredStep.x, opacity: 1 }
                  : {
                      width: currentStep.width,
                      x: currentStep.x,
                      opacity: 1,
                      transition: {
                        opacity: { ease: "easeInOut", duration: 0.4 },
                      },
                    }
              }
              transition={{ ease: "easeInOut", duration: 0.2 }}
              className={joinClassNames(
                "absolute h-5 sm:h-6 top-0 pointer-events-none",
                isShopsite
                  ? "rounded-lg bg-indigo-100"
                  : "rounded-[var(--radius-3)] bg-[color:var(--accent-a3)]"
              )}
            />
          </ol>
        </nav>
      ) : (
        <></>
      )}
    </div>
  );
}
